import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Download,
  User,
  Phone,
  MapPin,
  Calendar,
  Bus,
  MessageCircle,
  Music,
  Sparkles
} from "lucide-react";
import emailjs from "@emailjs/browser";

import Dropdown from "../components/Dropdown";
import { generatePDF } from "../utils/pdfGenerator";
import { saveBookingToSheet } from "../utils/saveBooking";
import { buildWhatsappLink, formatSongRequest } from "../utils/whatsappMessage";
import { transportData } from "../components/TransportData";
import { transportDataGaza } from "../components/TransportDataGaza";

// EmailJS config
const EMAILJS_PUBLIC_KEY = "W-CL6ETbw0q5XEFYz";
const EMAILJS_SERVICE_ID = "service_nh9y863";
const EMAILJS_TEMPLATE_ID = "template_5avy99o";

// Google Sheet web app URL (see google-apps-script/bookings.gs)
const BOOKINGS_SHEET_URL = "https://script.google.com/macros/s/AKfycbzDa0E-Kkt-FleoPhenaT4aIoSwT_5M7q0BmH-wgAisq2xPoaoY8GoIQ_sSsxAXcBpqxw/exec";

const FieldError = ({ children }) => (
  <p className="mt-2 text-sm text-red-600">{children}</p>
);

// ["a", "b", "c"] -> "a, b and c"
const listItems = (items) =>
  items.length > 1
    ? `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`
    : items[0];

const BookingForm = ({ onBookingComplete }) => {
  const [formData, setFormData] = useState({
    fullName: "",
    cellphone: "",
    transportRoute: "",
    pickupPoint: "",
    dropoffLocation: "",
    travelDate: "",
    songName: "",
    songArtist: ""
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null); // null | "success" | "error"
  const [completedBooking, setCompletedBooking] = useState(null);

  // Pick the active data structure based on the current selection state
  let activeTransport = transportData; // default fallback

  if (formData.transportRoute === "Vaal → Gaza") {
    activeTransport = transportDataGaza;
  } else if (formData.transportRoute === "Vaal → JHB") {
    activeTransport = transportData;
  } else if (formData.transportRoute === "Vaal → Venda") {
    activeTransport = transportData;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;

    setCompletedBooking(null);
    setFormData((prev) => {
      const updatedData = { ...prev, [name]: value };

      // Clear dependent fields if the route itself is changed to prevent invalid combinations
      if (name === "transportRoute") {
        updatedData.pickupPoint = "";
        updatedData.dropoffLocation = "";
        updatedData.travelDate = "";
      }

      return updatedData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setEmailStatus(null);

    const bookingReference = `V2V-${Date.now().toString().slice(-8)}`;

    const bookingDetails = {
      ...formData,
      reference: bookingReference,
      totalPrice: `R${totalPrice}`,
      deposit: `R${activeTransport.pricing?.deposit || 0}`,
      songRequest: formatSongRequest(formData),
      bookingDate: new Date().toLocaleDateString("en-ZA")
    };

    // Save to the Google Sheet without holding up the booking if it fails
    saveBookingToSheet(BOOKINGS_SHEET_URL, bookingDetails).catch((error) => {
      console.error("Failed to save booking to Google Sheet:", error);
    });

    // Send booking notification email via EmailJS
    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          fullName: bookingDetails.fullName,
          cellphone: bookingDetails.cellphone,
          transportRoute: bookingDetails.transportRoute,
          pickupPoint: bookingDetails.pickupPoint,
          dropoffLocation: bookingDetails.dropoffLocation,
          travelDate: bookingDetails.travelDate,
          reference: bookingDetails.reference,
          totalPrice: bookingDetails.totalPrice,
          deposit: bookingDetails.deposit,
          songRequest: bookingDetails.songRequest || "None",
          bookingDate: bookingDetails.bookingDate
        },
        EMAILJS_PUBLIC_KEY
      );
      setEmailStatus("success");
    } catch (error) {
      console.error("Failed to send booking email:", error);
      setEmailStatus("error");
      // Don't block the PDF download if email fails
    }

    // Generate and download the PDF receipt (unchanged behavior)
    generatePDF(bookingDetails, activeTransport);

    setCompletedBooking({
      reference: bookingDetails.reference,
      whatsappLink: buildWhatsappLink(bookingDetails, activeTransport),
      displayWhatsapp: activeTransport.contact.displayWhatsapp
    });

    if (onBookingComplete) {
      onBookingComplete(bookingDetails);
    }

    setIsSubmitting(false);
  };

  const totalPrice =
    activeTransport.pricing?.dropoffPrices?.[formData.dropoffLocation] ||
    activeTransport.pricing?.totalPrice ||
    0;

  const missingForDropoff = [
    !formData.fullName && "full name",
    !formData.transportRoute && "transport route",
    !formData.travelDate && "travel date"
  ].filter(Boolean);

  const missingForSubmit = [
    !formData.fullName && "full name",
    !formData.cellphone && "cellphone number",
    !formData.transportRoute && "transport route",
    !formData.pickupPoint && "pickup point",
    !formData.travelDate && "travel date",
    !formData.dropoffLocation && "drop-off location"
  ].filter(Boolean);

  const isFormValid = missingForSubmit.length === 0;

  const inputStyle = `
    w-full
    h-14
    px-5
    rounded-xl
    border
    border-gray-200
    bg-white
    outline-none
    focus:ring-4
    focus:ring-blue-100
    focus:border-blue-500
  `;

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white rounded-3xl shadow-xl p-6 md:p-10 border border-gray-100"
    >
      {/* HEADER */}
      <div className="text-center mb-10">
        <div className="mx-auto mb-4 w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center">
          <Bus className="text-white" />
        </div>
        <h2 className="text-3xl font-bold">Book Your Seat</h2>
        <p className="text-gray-500 mt-2">Complete your details below</p>
      </div>

      {/* NAME */}
      <div className="mb-6">
        <label className="flex gap-2 items-center font-semibold mb-2">
          <User size={18} />
          Full Names *
        </label>
        <input
          className={inputStyle}
          name="fullName"
          placeholder="Enter your full name"
          value={formData.fullName}
          onChange={handleChange}
        />
      </div>

      {/* PHONE */}
      <div className="mb-6">
        <label className="flex gap-2 items-center font-semibold mb-2">
          <Phone size={18} />
          Cellphone Number *
        </label>
        <input
          className={inputStyle}
          name="cellphone"
          placeholder="Enter cellphone number"
          value={formData.cellphone}
          onChange={handleChange}
        />
      </div>

      {/* TRANSPORT ROUTE */}
      <div className="mb-6">
        <label className="flex gap-2 items-center font-semibold mb-2">
          <Bus size={18} />
          Which transport Route Are You Looking For? *
        </label>
        <Dropdown
          className={inputStyle}
          name="transportRoute"
          value={formData.transportRoute}
          options={transportData.pickupTransport || []}
          placeholder="Select transport"
          onChange={handleChange}
        />
      </div>

      {/* PICKUP */}
      <div className="mb-6">
        <label className="flex gap-2 items-center font-semibold mb-2">
          <MapPin size={18} />
          Pickup Point *
        </label>
        <Dropdown
          className={inputStyle}
          name="pickupPoint"
          value={formData.pickupPoint}
          options={activeTransport.pickupPoints || []}
          placeholder="Select pickup point"
          onChange={handleChange}
          disabled={!formData.transportRoute}
        />
        {!formData.transportRoute && (
          <FieldError>Choose a transport route before you pick a pickup point.</FieldError>
        )}
      </div>

      {/* DATE */}
      <div className="mb-6">
        <label className="flex gap-2 items-center font-semibold mb-2">
          <Calendar size={18} />
          Travel Date *
        </label>
        <Dropdown
          className={inputStyle}
          name="travelDate"
          value={formData.travelDate}
          options={activeTransport.travelDates || []}
          placeholder="Select date"
          onChange={handleChange}
          disabled={!formData.transportRoute}
        />
        {!formData.transportRoute && (
          <FieldError>Choose a transport route before you pick a travel date.</FieldError>
        )}
      </div>

      {/* DROP OFF */}
      <div className="mb-8">
        <label className="flex gap-2 items-center font-semibold mb-3">
          <MapPin size={18} />
          Drop-off Location *
        </label>
        <div className="space-y-3">
          {missingForDropoff.length === 0 ? (
            (activeTransport.dropoffLocations || []).map((location) => (
              <label
                key={location}
                className={`
                  flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition
                  ${
                    formData.dropoffLocation === location
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-200"
                  }
                `}
              >
                <input
                  type="radio"
                  name="dropoffLocation"
                  value={location}
                  checked={formData.dropoffLocation === location}
                  onChange={handleChange}
                />
                <span>{location}</span>
              </label>
            ))
          ) : (
            <FieldError>
              Add your {listItems(missingForDropoff)} before you pick a drop-off location.
            </FieldError>
          )}
        </div>
      </div>

      {/* SONG REQUEST (OPTIONAL) */}
      <div className="mb-8">
        <label className="flex gap-2 items-center font-semibold mb-2">
          <Music size={18} />
          Song Request (optional)
        </label>
        <p className="text-sm text-gray-500 mb-3">
          Add a song and we'll play the requests as we leave.
        </p>
        <div className="grid md:grid-cols-2 gap-4">
          <input
            className={inputStyle}
            name="songName"
            aria-label="Song name"
            placeholder="Song name"
            maxLength={100}
            value={formData.songName}
            onChange={handleChange}
          />
          <input
            className={inputStyle}
            name="songArtist"
            aria-label="Artist"
            placeholder="Artist"
            maxLength={100}
            value={formData.songArtist}
            onChange={handleChange}
          />
        </div>
      </div>

      {/* PRICE SUMMARY */}
      <div className="bg-blue-50 rounded-2xl p-6 mb-8">
        <div className="flex gap-2 font-bold mb-5">
          <Sparkles size={18} />
          Price Summary
        </div>
        <div className="flex justify-between mb-3">
          <span>Total Price</span>
          <strong className="text-xl">
            R{totalPrice}
          </strong>
        </div>
        <div className="flex justify-between">
          <span>Deposit Required</span>
          <strong className="text-blue-600 text-xl">
            R{activeTransport.pricing?.deposit || 0}
          </strong>
        </div>
      </div>

      {/* BOOKING COMPLETE */}
      {completedBooking && (
        <div className="mb-6 p-5 rounded-xl bg-green-50 border border-green-200">
          <p className="font-semibold text-green-800">
            Booking {completedBooking.reference} received. Your receipt has downloaded.
          </p>
          <p className="text-sm text-green-700 mt-1 mb-4">
            Text us on WhatsApp about your booking. Your details are already filled in.
          </p>
          <a
            href={completedBooking.whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full h-12 rounded-xl font-bold flex items-center justify-center gap-2 bg-green-600 text-white hover:bg-green-700 transition"
          >
            <MessageCircle size={20} />
            WhatsApp {completedBooking.displayWhatsapp}
          </a>
        </div>
      )}

      {/* EMAIL STATUS FEEDBACK */}
      {emailStatus === "error" && (
        <div className="mb-6 p-4 rounded-xl bg-yellow-50 border border-yellow-200 text-yellow-700 text-sm">
          Your receipt downloaded, but the booking notification email failed to send.
        </div>
      )}

      {/* BUTTON */}
      <button
        disabled={!isFormValid || isSubmitting}
        className={`
          w-full h-14 rounded-xl font-bold flex items-center justify-center gap-3 transition
          ${
            isFormValid && !isSubmitting
              ? "bg-blue-600 text-white hover:bg-blue-700"
              : "bg-gray-200 text-gray-400 cursor-not-allowed"
          }
        `}
      >
        {isSubmitting ? (
          "Processing..."
        ) : (
          <>
            <Download size={20} />
            Submit & Download Receipt
          </>
        )}
      </button>
      {!isFormValid && (
        <FieldError>Add your {listItems(missingForSubmit)} to submit your booking.</FieldError>
      )}
      <p className="mt-3 text-center text-xs text-gray-500">
        We use your details to manage your booking. Read our{" "}
        <Link to="/privacy" className="underline">Privacy Policy</Link>.
      </p>
    </form>
  );
};

export default BookingForm;
